import { useState } from "react";
import { addOverride } from "../services/availabilityService";
import { apiErrorMessage, durationText, friendlyDateTime } from "../utils/workingHoursUtils";

interface Props {
  restaurantId: string;
  onClose: () => void;
  onSaved: () => void;
}

type Kind = "closed" | "open";
type Range = "allDay" | "window";

const CLOSED_REASONS = ["Resmî tatil", "Bayram", "Bakım / tadilat", "Personel izni", "Özel etkinlik"];
const OPEN_REASONS = ["Özel gün", "Bayram açılışı", "Etkinlik", "Ek mesai"];

const pad = (n: number) => String(n).padStart(2, "0");
const toDateValue = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toDateTimeValue = (d: Date) => `${toDateValue(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

export default function AddOverrideModal({ restaurantId, onClose, onSaved }: Props) {
  const now = new Date();
  const inTwoHours = new Date(now.getTime() + 2 * 3600000);
  inTwoHours.setMinutes(0, 0, 0);
  const inFourHours = new Date(inTwoHours.getTime() + 2 * 3600000);

  const [kind, setKind] = useState<Kind>("closed");
  const [range, setRange] = useState<Range>("allDay");
  const [startDate, setStartDate] = useState(toDateValue(now));
  const [endDate, setEndDate] = useState(toDateValue(now));
  const [startAt, setStartAt] = useState(toDateTimeValue(inTwoHours));
  const [endAt, setEndAt] = useState(toDateTimeValue(inFourHours));
  const [openEnded, setOpenEnded] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Saatler tarayıcının yerel saatiyle girilir (panel Kıbrıs'ta kullanılıyor); API'ye UTC gider.
  let start: Date | null = null;
  let end: Date | null = null;
  if (range === "allDay") {
    start = startDate ? new Date(`${startDate}T00:00`) : null;
    const last = endDate ? new Date(`${endDate}T00:00`) : null;
    if (last) { end = new Date(last); end.setDate(last.getDate() + 1); }
  } else {
    start = startAt ? new Date(startAt) : null;
    end = openEnded ? null : endAt ? new Date(endAt) : null;
  }

  let validation = "";
  if (!start || isNaN(start.getTime())) validation = "Başlangıç seçin.";
  else if (range === "window" && !openEnded && (!end || isNaN(end.getTime()))) validation = "Bitiş seçin.";
  else if (end && end <= start) validation = range === "allDay" ? "Bitiş günü başlangıçtan önce olamaz." : "Bitiş, başlangıçtan sonra olmalı.";
  else if (end && end <= now) validation = "Geçmişte kalan bir aralık eklenemez.";

  const submit = async () => {
    if (validation || !start) { setError(validation); return; }
    setSaving(true);
    setError("");
    try {
      await addOverride({
        restourantId: restaurantId,
        startsAtUtc: start.toISOString(),
        endsAtUtc: end ? end.toISOString() : null,
        isClosed: kind === "closed",
        reason: reason.trim() || undefined,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Kayıt eklenemedi."));
    } finally {
      setSaving(false);
    }
  };

  const reasons = kind === "closed" ? CLOSED_REASONS : OPEN_REASONS;
  const accent = kind === "closed" ? "#dc2626" : "#059669";

  return (
    <div className="wh-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="wh-modal" style={{ width: "min(96vw, 480px)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 36, height: 36, borderRadius: 10, background: kind === "closed" ? "#fef2f2" : "#ecfdf5", color: accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className={`bi ${kind === "closed" ? "bi-calendar-x-fill" : "bi-calendar-check-fill"}`}></i>
            </span>
            Özel Gün / Kapanış Ekle
          </div>
          <button onClick={onClose} disabled={saving} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "1.15rem" }}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Tür */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {([
              ["closed", "Kapalı olacak", "Tatil, bakım, izin", "bi-calendar-x", "#dc2626", "#fef2f2", "#fecaca"],
              ["open", "Özel açılış", "Programda kapalı olsa da açık", "bi-calendar-check", "#059669", "#ecfdf5", "#a7f3d0"],
            ] as [Kind, string, string, string, string, string, string][]).map(([k, title, sub, icon, color, bg, border]) => (
              <button key={k} type="button" onClick={() => { setKind(k); setReason(""); }}
                style={{
                  textAlign: "left", borderRadius: 12, padding: "10px 12px", cursor: "pointer",
                  border: `1.5px solid ${kind === k ? color : "#e5e7eb"}`, background: kind === k ? bg : "#fff",
                  boxShadow: kind === k ? `0 0 0 3px ${border}55` : "none",
                }}>
                <div style={{ fontWeight: 800, fontSize: ".84rem", color: kind === k ? color : "#374151" }}><i className={`bi ${icon} me-1`}></i>{title}</div>
                <div style={{ fontSize: ".7rem", color: "#6b7280", marginTop: 2 }}>{sub}</div>
              </button>
            ))}
          </div>

          {/* Aralık */}
          <div>
            <div className="wh-chips" style={{ marginBottom: 10 }}>
              <button type="button" className={`wh-chip${range === "allDay" ? " wh-chip--active" : ""}`} onClick={() => setRange("allDay")}>
                <i className="bi bi-calendar-range"></i>Tüm gün
              </button>
              <button type="button" className={`wh-chip${range === "window" ? " wh-chip--active" : ""}`} onClick={() => setRange("window")}>
                <i className="bi bi-clock"></i>Saat aralığı
              </button>
            </div>

            {range === "allDay" ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <span className="wh-label">İlk gün</span>
                  <input type="date" className="wh-input" min={toDateValue(now)} value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); if (endDate < e.target.value) setEndDate(e.target.value); }} />
                </div>
                <div>
                  <span className="wh-label">Son gün</span>
                  <input type="date" className="wh-input" min={startDate} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <span className="wh-label">Başlangıç</span>
                    <input type="datetime-local" className="wh-input" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
                  </div>
                  <div>
                    <span className="wh-label">Bitiş</span>
                    <input type="datetime-local" className="wh-input" value={endAt} min={startAt} disabled={openEnded}
                      onChange={(e) => setEndAt(e.target.value)} style={openEnded ? { background: "#f9fafb", color: "#9ca3af" } : undefined} />
                  </div>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: ".8rem", fontWeight: 600, color: "#374151", cursor: "pointer" }}>
                  <input type="checkbox" checked={openEnded} onChange={(e) => setOpenEnded(e.target.checked)} />
                  Bitişi belli değil (elle kaldırılana kadar)
                </label>
              </>
            )}
          </div>

          {/* Sebep */}
          <div>
            <span className="wh-label">Sebep <span style={{ fontWeight: 500, color: "#9ca3af" }}>(müşteriye gösterilir, opsiyonel)</span></span>
            <div className="wh-chips" style={{ marginBottom: 8 }}>
              {reasons.map((r) => (
                <button key={r} type="button" className={`wh-chip${reason === r ? " wh-chip--active" : ""}`} onClick={() => setReason(reason === r ? "" : r)}>{r}</button>
              ))}
            </div>
            <input className="wh-input" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ya da kendiniz yazın…" />
          </div>

          {/* Özet */}
          {!validation && start ? (
            <div className={`wh-note ${kind === "closed" ? "wh-note--warn" : "wh-note--info"}`}>
              <i className="bi bi-info-circle-fill"></i>
              <span>
                Restoran <b>{friendlyDateTime(start)}</b>
                {end ? <> – <b>{friendlyDateTime(end)}</b> ({durationText(end.getTime() - start.getTime())})</> : <> itibarıyla <b>süresiz</b></>}{" "}
                {kind === "closed" ? <>haftalık programı ne derse desin <b>kapalı</b> olacak.</> : <>haftalık programı kapalı dese de <b>açık</b> olacak.</>}
                {!end && kind === "closed" && <> Kendiliğinden kalkmaz, unutulmamalı.</>}
              </span>
            </div>
          ) : validation ? (
            <div className="wh-hint" style={{ color: "#b45309", marginTop: 0 }}><i className="bi bi-exclamation-circle me-1"></i>{validation}</div>
          ) : null}

          {error && <div className="wh-note wh-note--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button className="wh-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
          <button className={`wh-btn ${kind === "closed" ? "wh-btn--danger" : "wh-btn--success"}`} onClick={submit} disabled={saving || !!validation}>
            {saving ? <><span className="spinner-border spinner-border-sm"></span>Kaydediliyor…</> : <><i className="bi bi-check-lg"></i>Kaydet</>}
          </button>
        </div>
      </div>
    </div>
  );
}
